import { classNames } from "../utilities"
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/outline"
import * as Sentry from "@sentry/nextjs"
import Script from "next/script"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"

interface TurnstileRenderOptions {
  sitekey: string
  theme?: "auto" | "light" | "dark"
  size?: "normal" | "flexible" | "compact"
  callback?: (token: string) => void
  "error-callback"?: () => void
  "expired-callback"?: () => void
}

declare global {
  interface Window {
    turnstile?: {
      ready: (callback: () => void) => void
      render: (
        container: HTMLElement | string,
        options: TurnstileRenderOptions
      ) => string
      remove: (widgetId: string) => void
    }
  }
}

interface FormData {
  firstName: string
  lastName: string
  email: string
  company: string
  enrolledCustomers: string
  phone: string
  message: string
}

interface Props {
  page: string
}

export function ContactUs({ page }: Props) {
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
  const [showSuccess, setShowSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileError, setTurnstileError] = useState("")
  const [isTurnstileScriptLoaded, setIsTurnstileScriptLoaded] = useState(false)
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null)
  const turnstileWidgetIdRef = useRef<string | null>(null)
  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
  } = useForm<FormData>()

  const renderTurnstile = useCallback(() => {
    if (!turnstileSiteKey) {
      setTurnstileError(
        "Form protection is currently unavailable. Please try again later."
      )
      return
    }

    if (
      !isTurnstileScriptLoaded ||
      !turnstileContainerRef.current ||
      !window.turnstile
    ) {
      return
    }

    setTurnstileToken("")
    setTurnstileError("")

    if (turnstileWidgetIdRef.current) {
      window.turnstile.remove(turnstileWidgetIdRef.current)
      turnstileWidgetIdRef.current = null
    }

    turnstileContainerRef.current.innerHTML = ""

    turnstileWidgetIdRef.current = window.turnstile.render(
      turnstileContainerRef.current,
      {
        sitekey: turnstileSiteKey,
        theme: "auto",
        size: "flexible",
        callback: (token) => {
          setTurnstileToken(token)
          setTurnstileError("")
        },
        "error-callback": () => {
          setTurnstileToken("")
          setTurnstileError("Security check failed. Please try again.")
        },
        "expired-callback": () => {
          setTurnstileToken("")
          setTurnstileError(
            "Security check expired. Please complete it again."
          )
        },
      }
    )
  }, [isTurnstileScriptLoaded, turnstileSiteKey])

  const onSubmit = async ({
    firstName,
    lastName,
    email,
    company,
    enrolledCustomers,
    phone,
    message,
  }: FormData) => {
    if (!turnstileToken) {
      setTurnstileError("Please complete the security check before submitting.")
      return
    }

    try {
      setShowSuccess(false)
      setError(false)
      setTurnstileError("")

      const response = await fetch("/api/contact-us", {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        body: JSON.stringify({
          page,
          firstName,
          lastName,
          email,
          company,
          enrolledCustomers,
          phoneNumber: phone,
          message,
          turnstileToken,
        }),
      })

      const data = (await response.json().catch(() => null)) as
        | { message?: string }
        | null

      if (!response.ok) {
        if (data?.message) {
          setTurnstileError(data.message)
        }
        throw new Error(data?.message || "Unable to submit form")
      }

      setShowSuccess(true)
    } catch (err) {
      const _error = err as Error
      setError(true)
      Sentry.captureException(err)
      console.log(_error)
    } finally {
      renderTurnstile()
    }
  }

  useEffect(() => {
    reset()
  }, [showSuccess, reset])

  useEffect(() => {
    renderTurnstile()
  }, [renderTurnstile])

  useEffect(() => {
    return () => {
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.remove(turnstileWidgetIdRef.current)
      }
    }
  }, [])

  return (
    <div className="space-y-6">
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onLoad={() => setIsTurnstileScriptLoaded(true)}
        onError={() =>
          setTurnstileError(
            "Form protection could not be loaded. Please try again later."
          )
        }
      />
      {showSuccess && (
        <div className="rounded-md bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon
                className="h-5 w-5 text-green-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Thank you for contacting us. We will reply to your email as soon
                as possible.
              </p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircleIcon
                className="h-5 w-5 text-red-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">
                There was an error with your submission. Please try again.
              </p>
            </div>
          </div>
        </div>
      )}

      <form
        className="grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-8"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="col-span-2 sm:cols-span-1">
          <label
            htmlFor="firstName"
            className="block text-sm font-medium text-gray-700"
          >
            First name
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              id="firstName"
              type="text"
              placeholder="John"
              className={classNames(
                errors.firstName
                  ? "focus:ring-red-500 focus:border-red-500"
                  : "focus:ring-primary focus:border-primary",
                "py-3 px-4 block w-full shadow-sm border-gray-300 rounded-md placeholder-gray-400"
              )}
              {...register("firstName", { required: true })}
            />
            {errors.firstName && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ExclamationCircleIcon
                  className="h-5 w-5 text-red-500"
                  aria-hidden="true"
                />
              </div>
            )}
          </div>
          {errors.firstName && (
            <p className="mt-2 text-sm text-red-600">First name is required</p>
          )}
        </div>
        <div className="col-span-1 sm:col-span-2">
          <label
            htmlFor="lastName"
            className="block text-sm font-medium text-gray-700"
          >
            Last name
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              id="lastName"
              type="text"
              placeholder="Smith"
              className={classNames(
                errors.lastName
                  ? "focus:ring-red-500 focus:border-red-500"
                  : "focus:ring-primary focus:border-primary",
                "py-3 px-4 block w-full shadow-sm border-gray-300 rounded-md placeholder-gray-400"
              )}
              {...register("lastName", { required: true })}
            />
            {errors.lastName && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ExclamationCircleIcon
                  className="h-5 w-5 text-red-500"
                  aria-hidden="true"
                />
              </div>
            )}
          </div>
          {errors.lastName && (
            <p className="mt-2 text-sm text-red-git600">
              Last name is required
            </p>
          )}
        </div>
        {page === "partner" && (
          <>
            <div className="col-span-2">
              <label
                htmlFor="company"
                className="block text-sm font-medium text-gray-700"
              >
                Company
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  id="company"
                  type="text"
                  placeholder="Acme Inc"
                  className={classNames(
                    errors.company
                      ? "focus:ring-red-500 focus:border-red-500"
                      : "focus:ring-primary focus:border-primary",
                    "py-3 px-4 block w-full shadow-sm border-gray-300 rounded-md placeholder-gray-400"
                  )}
                  {...register("company", { required: true })}
                />
                {errors.company && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
              {errors.company && (
                <p className="mt-2 text-sm text-red-600">Company is required</p>
              )}
            </div>
            <div className="col-span-2">
              <label
                htmlFor="enrolledCustomers"
                className="block text-sm font-medium text-gray-700"
              >
                About how many customers are currently enrolled on your platform?
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  id="enrolledCustomers"
                  type="text"
                  inputMode="numeric"
                  placeholder="500"
                  className={classNames(
                    errors.enrolledCustomers
                      ? "focus:ring-red-500 focus:border-red-500"
                      : "focus:ring-primary focus:border-primary",
                    "py-3 px-4 block w-full shadow-sm border-gray-300 rounded-md placeholder-gray-400"
                  )}
                  {...register("enrolledCustomers", { required: page === "partner" })}
                />
                {errors.enrolledCustomers && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500"
                      aria-hidden="true"
                    />
                  </div>
                )}
              </div>
              {errors.enrolledCustomers && (
                <p className="mt-2 text-sm text-red-600">
                  Please enter your platform enrollment.
                </p>
              )}
            </div>
          </>
        )}
        <div className="col-span-2">
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              id="email"
              type="email"
              placeholder="you@email.com"
              className={classNames(
                errors.email
                  ? "focus:ring-red-500 focus:border-red-500"
                  : "focus:ring-primary focus:border-primary",
                "py-3 px-4 block w-full shadow-sm border-gray-300 rounded-md placeholder-gray-400"
              )}
              {...register("email", { required: true })}
            />
            {errors.email && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ExclamationCircleIcon
                  className="h-5 w-5 text-red-500"
                  aria-hidden="true"
                />
              </div>
            )}
          </div>
          {errors.email && (
            <p className="mt-2 text-sm text-red-600">Email is required</p>
          )}
        </div>
        <div className="col-span-2">
          <div className="flex justify-between">
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-gray-700"
            >
              Phone
            </label>
            <span className="text-sm text-gray-500">Optional</span>
          </div>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              id="phone"
              type="text"
              placeholder="555-987-6543"
              className="placeholder-gray-400 py-3 px-4 block w-full shadow-sm focus:ring-primary focus:border-primary border-gray-300 rounded-md"
              {...register("phone")}
            />
          </div>
        </div>
        <div className="col-span-2">
          <label
            htmlFor="message"
            className="block text-sm font-medium text-gray-700"
          >
            Message
          </label>
          <div className="mt-1">
            <textarea
              id="message"
              rows={4}
              placeholder="Your message"
              className={classNames(
                errors.message
                  ? "focus:ring-red-500 focus:border-red-500"
                  : "focus:ring-primary focus:border-primary",
                "py-3 px-4 block w-full shadow-sm border border-gray-300 rounded-md placeholder-gray-400"
              )}
              {...register("message", { required: true })}
            />
            {errors.message && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <ExclamationCircleIcon
                  className="h-5 w-5 text-red-500"
                  aria-hidden="true"
                />
              </div>
            )}
            {errors.message && (
              <p className="mt-2 text-sm text-red-600">Message is required</p>
            )}
          </div>
        </div>
        <div className="col-span-2">
          <div
            ref={turnstileContainerRef}
            className="min-h-[65px]"
            aria-live="polite"
          />
          {turnstileError && (
            <p className="mt-2 text-sm text-red-600">{turnstileError}</p>
          )}
        </div>
        <div className="sm:col-span-2 space-x-4">
          <button
            type="submit"
            disabled={isSubmitting || !turnstileToken || !turnstileSiteKey}
            className={classNames(
              "btn-primary w-full",
              isSubmitting || !turnstileToken || !turnstileSiteKey
                ? "cursor-not-allowed opacity-60"
                : ""
            )}
          >
            {isSubmitting ? (
              <>
                <span>Submitting</span>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </>
            ) : (
              <>
                <span>Let's talk</span>
                <ArrowRightIcon className="h-4 w-4 inline" />
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
